import { NextPage } from 'next';
import React, { FC } from 'react';
import { useIntl } from 'react-intl';
import JsonLd from '../../../components/layouts/JsonLd';
import MetaTags from '../../../components/layouts/MetaTags';
import MerchantReviewsPage from '../../../templates/merchants/reviews/IndexTemplate/MerchantReviewsPage';
import { getCategory } from '../../../infrastructure/api/backend/explore_api';
import { CategoryEntity } from '../../../interfaces/explore/CategoryEntity';
import { MerchantDetail } from '../../../interfaces/merchants';
import { MerchantReviewsResponse } from '../../../interfaces/merchants/reviews';
import {
  fetchMerchantDetail,
  fetchMerchantReviewsForSSR,
} from '../../../infrastructure/api';
import { injectAppContextGetServerSideProps } from '../../_app';
import ErrorTemplate from '../../../templates/ErrorTemplate';
import { MerchantSite } from '../../../components/MerchantSite';
import { usePushMerchantDataLayerEffect } from '../../../hooks/gtm';
import { buildSiteUrl } from 'src/utils/request-url';

const REVIEW_PER_PAGE = 20;

type Props = {
  max: number;
  merchantDetail?: MerchantDetail;
  category?: CategoryEntity;
  reviewsData?: MerchantReviewsResponse;
  isUnpublishedSite: boolean;
  isCoubicApp: boolean;
};

const ReviewComponent: FC<Props> = ({
  merchantDetail,
  max,
  category,
  reviewsData,
  isCoubicApp,
}) => {
  const intl = useIntl();
  usePushMerchantDataLayerEffect({ visit_merchant_id: merchantDetail?.id });

  if (merchantDetail && category && reviewsData) {
    return (
      <>
        <MetaTags
          title={intl.formatMessage(
            { id: 'reviews.index.meta.title' },
            { name: merchantDetail.display_name },
          )}
          description={
            merchantDetail.seo_setting?.description ||
            merchantDetail.description
          }
          keywords={merchantDetail.seo_setting?.keyword || ''}
          thumbnailUrl={merchantDetail.cover_image.url || ''}
          currentPath={buildSiteUrl(`/reviews`)}
          noIndex={merchantDetail.seo_setting?.no_index}
          googleAnalyticsFourTrackingId={
            merchantDetail.available_google_analytics_four_tracking_id
          }
          faviconImageUrl={merchantDetail?.favicon_image?.url}
        />
        <JsonLd merchant={merchantDetail} reviews={reviewsData.data} />
        <MerchantReviewsPage
          merchantDetail={merchantDetail}
          max={max}
          category={category}
          reviewsData={reviewsData}
          isCoubicApp={isCoubicApp}
        />
      </>
    );
  }
  return <ErrorTemplate statusCode={404} />;
};

const MerchantReviews: NextPage<Props> = (props: Props) => {
  const { merchantDetail, isUnpublishedSite } = props;

  return (
    <MerchantSite
      isUnPublished={isUnpublishedSite}
      isMerchantInfoExist={!!merchantDetail}
      logoImage={merchantDetail?.logo_image || null}
      merchantPublicId={merchantDetail?.public_id || null}
      displayName={merchantDetail?.display_name || null}
      isCoubicApp={props.isCoubicApp}
    >
      <ReviewComponent {...props} />
    </MerchantSite>
  );
};

export const getServerSideProps = injectAppContextGetServerSideProps<Props>(
  async (context) => {
    const { query, session, token, requestId, isCoubicApp } = context;
    const merchantPublicId = String(query.publicId);

    const [merchantDetailResponse, reviews] = await Promise.all([
      // Layoutで取得している部分を徐々に置き換え
      fetchMerchantDetail({ session, token, requestId, merchantPublicId }),
      fetchMerchantReviewsForSSR({
        session,
        token,
        requestId,
        merchantPublicId,
        page: 1,
        perPage: REVIEW_PER_PAGE,
      }),
    ]);
    if (merchantDetailResponse.redirect) {
      return { redirect: merchantDetailResponse.redirect };
    }
    const { merchant, isUnpublishedSite } = merchantDetailResponse;

    const category = merchant
      ? await getCategory(merchant.category_id)
      : undefined;
    const props: Props = {
      merchantDetail: merchant,
      category,
      reviewsData: reviews,
      max: REVIEW_PER_PAGE,
      isUnpublishedSite,
      isCoubicApp,
    };
    return { props };
  },
);

export default MerchantReviews;
